"use client";

import { useState, useCallback } from "react";
import FileUploader from "./FileUploader";
import PdfThumbnailGrid from "./PdfThumbnailGrid";
import ProcessingOverlay from "./ProcessingOverlay";
import DownloadResult from "./DownloadResult";
import SignupGateModal from "./SignupGateModal";
import UsageLimitBanner from "./UsageLimitBanner";
import { useToolFetch } from "@/hooks/useToolFetch";

export default function SplitPdfTool() {
  const [file, setFile] = useState<File | null>(null);
  const [selectedPages, setSelectedPages] = useState<Set<number>>(new Set());
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState<{ url: string; size: number } | null>(null);
  const [error, setError] = useState("");
  const { toolFetch, quotaError } = useToolFetch();

  const handleFilesSelected = useCallback((files: File[]) => {
    setFile(files[0] || null);
    setSelectedPages(new Set());
    setResult(null);
    setError("");
  }, []);

  const split = useCallback(async () => {
    if (!file || selectedPages.size === 0) {
      setError("Select at least one page to extract");
      return;
    }
    setProcessing(true);
    setError("");

    try {
      const pages = Array.from(selectedPages).sort((a, b) => a - b).map((p) => p + 1).join(",");
      const formData = new FormData();
      formData.append("file", file);
      formData.append("pages", pages);

      const res = await toolFetch("/api/tools/split", { method: "POST", body: formData });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to split PDF");
      }

      const blob = await res.blob();
      setResult({ url: URL.createObjectURL(blob), size: blob.size });
    } catch (e: any) {
      setError(e.message || "Failed to split PDF");
    } finally {
      setProcessing(false);
    }
  }, [file, selectedPages]);

  const reset = useCallback(() => {
    setFile(null);
    setSelectedPages(new Set());
    setResult(null);
    setError("");
  }, []);

  if (result) {
    return <DownloadResult fileName="split.pdf" fileSize={result.size} downloadUrl={result.url} onReset={reset} />;
  }

  if (quotaError?.gate === "signup") return <SignupGateModal quotaError={quotaError} />;

  return (
    <div className="relative space-y-6">
      {processing && <ProcessingOverlay message="Extracting pages..." />}
      {quotaError?.gate === "limit" && <UsageLimitBanner quotaError={quotaError} />}

      {!file ? (
        <FileUploader accept=".pdf" onFilesSelected={handleFilesSelected} label="Drop your PDF here" description="Select pages to extract" />
      ) : (
        <>
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-700">Select pages to extract</p>
            <button onClick={reset} className="text-xs text-slate-400 hover:text-slate-600">
              Change file
            </button>
          </div>

          <PdfThumbnailGrid
            file={file}
            mode="select"
            selectedPages={selectedPages}
            onSelectionChange={setSelectedPages}
          />

          {error && <p className="text-sm text-red-600">{error}</p>}

          <button
            onClick={split}
            disabled={processing || selectedPages.size === 0}
            className="btn-primary w-full py-3"
          >
            Extract {selectedPages.size} page{selectedPages.size !== 1 ? "s" : ""}
          </button>
        </>
      )}
    </div>
  );
}
